import { useMemo } from 'react'

import { Box, Button, Table, Text } from '@factorialco/deprecated-design-system'
import { QuotePricingLineItemFragment, TaxRateForSelectFragment } from 'generated/resources/graphql'

import MoneyField from 'modules/core/components/MoneyField'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { TaxRateEntitySelector } from 'modules/finance/components/TaxRateEntitySelector'

interface ItemRowProductProps {
  item: QuotePricingLineItemFragment
  currency: string
  isDraftStatus: boolean
  onUpdate: (updates: Partial<QuotePricingLineItemFragment>) => void
  onDelete: () => void
  legalEntityCountry: string | undefined
}

const ItemRow = ({
  item,
  currency,
  isDraftStatus,
  onUpdate,
  onDelete,
  legalEntityCountry,
}: ItemRowProductProps) => {
  const {
    access: { locale },
  } = useCurrentSession()

  const currentTaxRate: TaxRateForSelectFragment | null = useMemo(() => {
    if (item.taxRateId && item.taxRatePercentage != null) {
      const taxRate: TaxRateForSelectFragment = {
        id: item.taxRateId,
        rate: item.taxRatePercentage,
        taxType: {
          countryCode: item.taxTypeCountry,
        },
      }
      return taxRate
    }
    return null
  }, [item.taxRateId, item.taxRatePercentage, item.taxTypeCountry])

  const handleQuantityChange = (value: string) => {
    const newQuantity = Number(value)
    const currentUnitPriceCents = item.unitPriceCents || 0
    const newBasePriceCents = Math.round(newQuantity * currentUnitPriceCents)
    onUpdate({
      quantity: newQuantity,
      basePriceCents: newBasePriceCents,
    })
  }

  const handleUnitPriceChange = (value: string) => {
    const newUnitPriceCents = Number(value) * 100
    const currentQuantity = item.quantity || 0
    const newBasePriceCents = Math.round(currentQuantity * newUnitPriceCents)
    onUpdate({
      unitPriceCents: newUnitPriceCents,
      basePriceCents: newBasePriceCents,
    })
  }

  const handleTaxRateChange = (taxRate: TaxRateForSelectFragment | null) => {
    const newTaxRatePercentage = taxRate ? taxRate.rate : 0
    onUpdate({
      taxRateId: taxRate?.id ?? null,
      taxRatePercentage: newTaxRatePercentage,
    })
  }

  return (
    <Table.Row>
      <Table.Cell paddingX="s12" colSpan={2}>
        <Box width="s300" paddingLeft="s12">
          {isDraftStatus ? (
            <TextField
              placeholder={i18n.t('common.name')}
              value={item.description || ''}
              onChange={(value) => onUpdate({ ['description']: value })}
            />
          ) : (
            <Text size="300" color="grey1000">
              {item.description || '-'}
            </Text>
          )}
        </Box>
      </Table.Cell>
      <Table.Cell paddingX="s12" colSpan={2}>
        <Box textAlign="right">
          {isDraftStatus ? (
            <Box flexDirection="row" justifyContent="flexEnd">
              <MoneyField
                placeholder="0"
                currency={'u'} // TODO: Translate
                onChange={handleQuantityChange}
                value={(item.quantity || 0).toString()}
              />
            </Box>
          ) : (
            <Text size="300" color="grey1000">
              {item.quantity || '-'}
            </Text>
          )}
        </Box>
      </Table.Cell>
      <Table.Cell paddingX="s12" colSpan={2}>
        <Box textAlign="right">
          {isDraftStatus ? (
            <Box flexDirection="row" justifyContent="flexEnd">
              <MoneyField
                placeholder="0"
                currency={currency}
                onChange={handleUnitPriceChange}
                value={((item.unitPriceCents || 0) / 100).toString()}
              />
            </Box>
          ) : (
            <Text size="300" color="grey1000">
              {MoneyWithCurrency({
                optionalDecimals: true,
                amount: item.unitPriceCents || 0,
                currency,
                locale,
              })}
            </Text>
          )}
        </Box>
      </Table.Cell>
      <Table.Cell paddingX="s12" colSpan={2}>
        <Box textAlign="right">
          {isDraftStatus ? (
            <TaxRateEntitySelector
              label={''}
              value={currentTaxRate}
              onChange={handleTaxRateChange}
              source={'system'}
              hideSearchIcon
              type={'vat'}
              country={legalEntityCountry}
            />
          ) : (
            <Text size="300" color="grey1000">
              {`${((item.taxRatePercentage || 0) * 100).toFixed(2)}%`}
            </Text>
          )}
        </Box>
      </Table.Cell>
      <Table.Cell paddingX="s12" colSpan={2}>
        <Box textAlign="right">
          <Text size="300" color="grey1000">
            {MoneyWithCurrency({
              optionalDecimals: true,
              amount: item.basePriceCents || 0,
              currency,
              locale,
            })}
          </Text>
        </Box>
      </Table.Cell>
      {isDraftStatus && (
        <Table.Cell paddingX="s12" colSpan={1} width={80}>
          <Button type="secondary" iconName="delete" onClick={onDelete} />
        </Table.Cell>
      )}
    </Table.Row>
  )
}

export default ItemRow
